import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useConversations } from 'providers/ConversationProvider';
import { useCampaignData } from 'services/campaign/useCampaignData';
import { useItemData } from 'services/item/useItemData';
import { usePublicUserData } from 'services/user/usePublicUserData';

import { MessengerState } from './messengerState';
import { buildDraftConversation, PendingConversation } from './pendingConversation';

export const usePendingConversation = () => {
  const { state }: { state: MessengerState } = useLocation();
  const navigate = useNavigate();
  const { createConversation } = useConversations();

  const [pendingConversation, setPendingConversation] = useState<PendingConversation>();

  const { content: publicUser } = usePublicUserData(
    pendingConversation?.context !== 'SUPPORT' ? pendingConversation?.interlocutorId ?? undefined : undefined
  );
  const { content: item } = useItemData(
    pendingConversation?.context === 'ITEM' ? pendingConversation.objectId : undefined
  );
  const { content: campaign } = useCampaignData(
    pendingConversation?.context === 'CAMPAIGN' ? pendingConversation.objectId : undefined
  );

  const draftConversation = useMemo(
    () => (pendingConversation ? buildDraftConversation(pendingConversation, publicUser, item, campaign) : undefined),
    [pendingConversation, publicUser, item, campaign]
  );

  const startPendingConversation = (pending: PendingConversation) => setPendingConversation(pending);

  const clearPendingConversation = () => setPendingConversation(undefined);

  const createFromPendingConversation = async (): Promise<number | undefined> => {
    if (!pendingConversation) {
      return;
    }

    const conversationId = await createConversation(
      { context: pendingConversation.context, contextualObjectId: pendingConversation.objectId },
      pendingConversation.interlocutorId ?? null
    );

    if (conversationId) {
      setPendingConversation(undefined);
      navigate('/messenger', { replace: true, state });
    }

    return conversationId;
  };

  const clearDraft = () => {
    setPendingConversation(undefined);
    navigate('/messenger', { replace: true });
  };

  return {
    pendingConversation,
    draftConversation,
    startPendingConversation,
    clearPendingConversation,
    createFromPendingConversation,
    clearDraft,
  };
};
